import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import invariant from 'tiny-invariant'
import {
  AggregatedCreditNote,
  LineItemGroup,
  ServerLineItem
} from 'CreditNotes/types'
import { creditNote } from 'CreditNotes/ports'
import { useEffect, useMemo, useState } from 'react'

export const useAggregatedCreditNote = (
  creditNoteId: string
): {
  creditNote: AggregatedCreditNote | null
} => {
  const [aggregatedCreditNote, setAggregatedCreditNote] =
    useState<AggregatedCreditNote | null>(null)

  invariant(creditNoteId, 'Credit note ID is required')

  const creditNoteQuery = useQuery(dashboardv99990101Client.getCreditNote, {
    id: creditNoteId
  })

  // useLoadAllItemsQuery types apiFn param as object, which is incompatible with
  // endpoints that have required params. The params are passed separately via
  // queryParams. One to fix up in the future!
  const lineItemsInfiniteQuery = useLoadAllItemsQuery(
    //@ts-expect-error somewhat janky interface for the deprecated hook when there are required properties
    dashboardv99990101Client.getCreditNoteLineItems,
    {
      creditNoteId: creditNoteId,
      limit: 100
    }
  )

  const groupsInfiniteQuery = useLoadAllItemsQuery(
    //@ts-expect-error somewhat janky interface for the deprecated hook when there are required properties
    dashboardv99990101Client.getCreditNoteLineItemGroups,
    {
      creditNoteId: creditNoteId,
      limit: 100
    }
  )

  const allLineItems = useMemo(
    () =>
      (lineItemsInfiniteQuery.data?.pages.flatMap(page => page.items) ??
        []) as ServerLineItem[],
    [lineItemsInfiniteQuery.data]
  )

  const allGroups = useMemo(
    () =>
      (groupsInfiniteQuery.data?.pages.flatMap(page => page.items) ??
        []) as LineItemGroup[],
    [groupsInfiniteQuery.data]
  )

  const lineItemsSettled =
    lineItemsInfiniteQuery.isSuccess &&
    !lineItemsInfiniteQuery.isFetching &&
    !lineItemsInfiniteQuery.hasNextPage
  const groupsSettled =
    groupsInfiniteQuery.isSuccess &&
    !groupsInfiniteQuery.isFetching &&
    !groupsInfiniteQuery.hasNextPage

  // If we don't wait for all data to settle, we end up having flashes of old data when one part of
  // the aggregate is returned from the API and the others are pending.
  useEffect(() => {
    if (
      !creditNoteQuery.data ||
      !lineItemsSettled ||
      !groupsSettled ||
      creditNoteQuery.isRefetching
    ) {
      return
    }

    setAggregatedCreditNote(
      creditNote.fromServer(creditNoteQuery.data, allGroups, allLineItems)
    )
  }, [
    creditNoteQuery.data,
    creditNoteQuery.isRefetching,
    allLineItems,
    allGroups,
    lineItemsSettled,
    groupsSettled
  ])

  return {
    creditNote: aggregatedCreditNote
  }
}
